/**
 * NEXUS UI - AlertCallout
 * Attention-grabbing notification panels
 */

import React from 'react';
import type { BaseProps } from './types';

type AlertType = 'info' | 'success' | 'warning' | 'critical';

export interface AlertCalloutProps extends BaseProps {
  type: AlertType;
  title: string;
  message?: string;
  timestamp?: string;
  icon?: React.ReactNode;
  onDismiss?: () => void;
  actions?: React.ReactNode;
}

const alertConfig: Record<AlertType, { color: string; aura: string; icon: string }> = {
  info: { color: '#00d4ff', aura: 'rgba(0, 212, 255, 0.15)', icon: 'ℹ' },
  success: { color: '#00ff88', aura: 'rgba(0, 255, 136, 0.15)', icon: '✓' },
  warning: { color: '#ffaa00', aura: 'rgba(255, 170, 0, 0.15)', icon: '⚠' },
  critical: { color: '#ff3366', aura: 'rgba(255, 51, 102, 0.15)', icon: '✕' },
};

export const AlertCallout: React.FC<AlertCalloutProps> = ({
  type,
  title,
  message,
  timestamp,
  icon,
  onDismiss,
  actions,
  className = '',
  style,
}) => {
  const config = alertConfig[type];

  return (
    <div
      className={`nx-callout nx-callout--${type} ${className}`}
      style={{
        '--callout-color': config.color,
        '--callout-aura': config.aura,
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '16px 20px',
        background: 'var(--nx-glass-dark)',
        borderLeft: `3px solid ${config.color}`,
        clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)',
        animation: type === 'critical' ? 'nx-flicker 4s infinite' : 'none',
        ...style,
      } as React.CSSProperties}
    >
      {/* Gradient overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(90deg, ${config.aura} 0%, transparent 50%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Scan line for critical alerts */}
      {type === 'critical' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${config.color}, transparent)`,
            animation: 'nx-scan 2s linear infinite',
          }}
        />
      )}

      {/* Icon */}
      <div
        className="nx-callout__icon"
        style={{
          flexShrink: 0,
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: config.color,
          fontSize: '16px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {icon || config.icon}
      </div>

      {/* Content */}
      <div className="nx-callout__content" style={{ flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
        <div
          className="nx-callout__title"
          style={{
            fontFamily: 'var(--nx-font-interface)',
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--nx-text-primary)',
            marginBottom: message ? '4px' : 0,
          }}
        >
          {title}
        </div>

        {message && (
          <div
            className="nx-callout__message"
            style={{
              fontFamily: 'var(--nx-font-interface)',
              fontSize: '14px',
              color: 'var(--nx-text-secondary)',
              lineHeight: 1.5,
            }}
          >
            {message}
          </div>
        )}

        {timestamp && (
          <div
            className="nx-callout__timestamp"
            style={{
              fontFamily: 'var(--nx-font-mono)',
              fontSize: '11px',
              color: 'var(--nx-text-ghost)',
              marginTop: '8px',
            }}
          >
            {timestamp}
          </div>
        )}

        {actions && (
          <div
            className="nx-callout__actions"
            style={{
              marginTop: '12px',
              display: 'flex',
              gap: '8px',
            }}
          >
            {actions}
          </div>
        )}
      </div>

      {/* Dismiss button */}
      {onDismiss && (
        <button
          onClick={onDismiss}
          style={{
            flexShrink: 0,
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: 'var(--nx-text-tertiary)',
            cursor: 'pointer',
            borderRadius: '4px',
            transition: 'all 0.15s ease-out',
            position: 'relative',
            zIndex: 1,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'var(--nx-text-primary)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--nx-text-tertiary)';
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

export default AlertCallout;
